import { type MouseEvent, useCallback } from "react";

import { useSelector } from "react-redux";
import { IconType } from "../../../common/components/icon/IconType";
import { usePackageRestrictionContext } from "../../../common/components/PackageRestrictionContext/PackageRestrictionContext";
import { AddToCartService } from "../../../AddToCart/AddToCartService";
import { useTranslation } from "react-i18next";
import { Button, type ButtonProps } from "~/common/components/Button/Button";
import { type AddToCartHookProps } from "~/AddToCart/hooks/useAddToCartNew";
import { type BaseProductElementProps } from "~/feature/Product/types";
import { useProductContext } from "~/feature/Product/ProductContext";
import { selectSearchQuery } from "~/Search/redux/searchSelectors";
import { SideButton } from "~/common/components/Button/SideButton";
import { DefaultButtonIcon } from "~/common/components/icon/Icon";
import type { AddToCartSource } from "~/feature/Analytics/types";
import { usePurchasePlace } from "~/feature/Analytics/ga/recommendations/RecommendationContexts";
import type { PurchasePlace, PurchaseType } from "~/feature/Analytics/ga/recommendations/types";

type AddToCartButtonProps = {
  sideButton?: boolean;
  actionVariant?: "cart" | "quote" | "salesman";
} & Omit<ButtonProps, "children">;
export const AddToCartButtonBase = (props: AddToCartButtonProps) => {
  const { t } = useTranslation();
  const { actionVariant = "cart", sideButton, ...rest } = props;

  let icon;
  let label;
  if (actionVariant === "cart") {
    icon = IconType.Cart;
    label = t("addToCartPopup.add.submitBtn");
  } else if (actionVariant === "quote") {
    icon = IconType.FileList;
    label = t("cartPage.btn.quote");
  } else {
    icon = IconType.Cart;
    label = t("basket.addToQuote");
  }

  if (sideButton) {
    return <SideButton icon={icon} data-test-id={"btn-add-to-wishlist"} {...rest} />;
  }

  return (
    <Button fullWidth={true} startIcon={<DefaultButtonIcon type={icon} />} data-test-id={"btn-add-to-cart"} {...rest}>
      {label}
    </Button>
  );
};

type WizardProps = Omit<AddToCartHookProps, "editMode" | "productCode" | "restrictionQuery"> & {
  source?: AddToCartSource;
};
interface OwnProps extends BaseProductElementProps {
  sideButton?: boolean;
  actionVariant?: "cart" | "quote";
  wizardConfig?: WizardProps;
  onAfterProductAdded?: () => void;
  reco_type?: PurchaseType;
  reco_place?: PurchasePlace;
}

type Props = OwnProps & Omit<ButtonProps, "children">;

export const BasicAddToCartButton = ({ reco_type, reco_place, ...props }: Props) => {
  const product = useProductContext(props);
  const page = usePurchasePlace();
  const { onClick, onAfterProductAdded, wizardConfig, disabled, ...rest } = props;

  const restrictedPackage = usePackageRestrictionContext();

  const searchQuery = useSelector(selectSearchQuery);

  const onClickCb = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      await AddToCartService.openAddToCartWizard(
        {
          productCode: product.code,
          restrictionQuery: restrictedPackage ?? "",
          ...wizardConfig,
        },
        { page, recommendationType: reco_type, recommendationPlace: reco_place },
      );
      onAfterProductAdded?.();
    },
    [onClick, product.code, restrictedPackage, searchQuery],
  );

  return <AddToCartButtonBase onClick={onClickCb} disabled={window.SCHA.config.disableAllWS || disabled} {...rest} />;
};
